// authSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
    registerUser,
    loginUser,
    googleLogin,
    LogoutUser,
    UpdateUserProfile,
    refreshToken,
    uploadUserImage,
    getAllLanguages,
    getAllCurrencies,
    getCurrencyById,
    getAllTimezones,
    UpdateClientGeneralSettings,
    createGeneralClientSetting,
    getUserGeneralSettingAll,
    getUserGeneralSetting,
    facebookLogin,
    appleLogin,
    getUserTickets,
    getAllTickets,
    createUserTickets,
    updateUserTickets,
    deleteUserTicket,
    deleteUserGeneralSetting,
    getUserConversations,
    UpdateUserConversation,
    deleteUserConversation,
    createConversation,
    deleteUserChat,
    UpdateUserChats,
    getUserChats,
    createChat,
    UpdateUserPassword,
    getAllJobs,
    getUserJobs,
    getJobById,
    getUserById,
    sendPhoneVerificationCode,
    verifyPhoneOTP,
    assignProfessionalWhatsApp,
    detectUserLocation,
} from "../Actions/AuthAction";
import { updateUserJob, deleteUserJob } from "../Actions/ClientActions";
import { AuthState } from "../../modules/shared/model/AuthInterfaces";

const initialState: AuthState = {
    userTickets: [],
    allTickets: {
        data: [],
        hasNextPage: false,
        totalCount: 0,
    },
    user: null,
    viewedUser: null, // Add field for viewing other users' profiles
    token: null,
    allgeneralSetting: [],
    generalSetting: null,
    isAuthenticated: false,
    error: null,
    rehydrated: false,
    languages: [],
    timezones: [],
    jobs: [],
    allJobs: {
        data: [],
        totalCount: 0,
        hasNextPage: false,
        currentPage: 1,
        totalPages: 1
    },
    currentJob: null,
    currencies: [],
    chats: null,
    conversations: [],
    locationData: null, // Add location detection data
};

// Language storage keys that should be preserved across login/logout
const LANGUAGE_STORAGE_KEYS = [
    "kokobeo_selected_language",
    "i18nextLng"
];

// Helper function to preserve language preferences
const preserveLanguagePreferences = () => {
    const preserved: Record<string, string> = {};
    LANGUAGE_STORAGE_KEYS.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
            preserved[key] = value;
        }
    });
    return preserved;
};

// Helper function to restore language preferences
const restoreLanguagePreferences = (preserved: Record<string, string>) => {
    Object.entries(preserved).forEach(([key, value]) => {
        localStorage.setItem(key, value);
    });
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        LogoutManually(state) {
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            
            // Preserve language preferences before clearing
            const preservedLanguage = preserveLanguagePreferences();
            
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.clear();
            
            // Restore language preferences after clearing
            restoreLanguagePreferences(preservedLanguage);
        },
        EmptyConversations(state) {
            state.conversations = [];
            state.chats = null;
        },
        rehydrateUser(state) {
            const token = localStorage.getItem("token");
            const user = localStorage.getItem("user");

            // Parse the token since it was stored with JSON.stringify
            state.token = token ? JSON.parse(token) : null;
            state.user = user ? JSON.parse(user) : null;
            state.isAuthenticated = !!state.token;
            state.rehydrated = true;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(registerUser.pending, state => {
                state.error = null;
            })
            .addCase(LogoutUser.fulfilled, (state, action) => {
                state.user = null;
                state.token = null;
                state.isAuthenticated = false;
                
                // Preserve language preferences before clearing
                const preservedLanguage = preserveLanguagePreferences();
                
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                localStorage.removeItem("refreshToken");
                
                // Clear all localStorage except language preferences
                // We'll restore language preferences after clearing
                const keysToRemove: string[] = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && !key.startsWith("i18next") && !key.startsWith("language")) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
                
                // Restore language preferences after clearing
                restoreLanguagePreferences(preservedLanguage);
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                const active_user_id = parseInt(action.payload?.user?.status?.id);
                if (active_user_id == 2) {
                    state.user = null;
                    state.token = null;
                    state.isAuthenticated = false;
                } else {
                    state.user = action.payload.user;
                    state.token = action.payload.token;
                    state.isAuthenticated = true;
                }
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.error = typeof action.payload === "string" ? action.payload : "Registration failed";
            })
            .addCase(refreshToken.fulfilled, (state, action) => {
                state.token = action.payload.token;
            })
            .addCase(facebookLogin.fulfilled, (state, action) => {
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.isAuthenticated = true;
            })
            .addCase(facebookLogin.rejected, (state, action) => {
                state.error = typeof action.payload === "string" ? action.payload : "Facebook Login failed";
            })
            .addCase(appleLogin.fulfilled, (state, action) => {
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.isAuthenticated = true;
            })
            .addCase(getAllJobs.fulfilled, (state, action) => {
                state.jobs = action.payload?.data || [];
                const totalCount = action.payload?.totalCount || 0;
                const currentPage = action.payload?.currentPage || 1;
                const totalPages = action.payload?.totalPages || Math.ceil(totalCount / 10) || 1;
                state.allJobs = {
                    data: action.payload?.data || [],
                    totalCount,
                    hasNextPage: currentPage < totalPages,
                    currentPage,
                    totalPages
                };
            })
            .addCase(getUserJobs.fulfilled, (state, action) => {
                state.jobs = action.payload?.data || [];
            })
            .addCase(getJobById.fulfilled, (state, action) => {
                // Store the fetched job for editing
                state.currentJob = action.payload;
            })
            .addCase(updateUserJob.fulfilled, (state, action) => {
                // Update the specific job in the jobs array
                const updatedJob = action.payload;
                const jobIndex = state.jobs.findIndex(job => job.id === updatedJob.id);
                if (jobIndex !== -1) {
                    state.jobs[jobIndex] = updatedJob;
                }
            })
            .addCase(deleteUserJob.fulfilled, (state, action) => {
                // Remove the deleted job from the jobs array
                const deletedJobId = action.payload.id;
                state.jobs = state.jobs.filter(job => job.id !== deletedJobId);
            })
            .addCase(appleLogin.rejected, (state, action) => {
                state.error = typeof action.payload === "string" ? action.payload : "Apple Login failed";
            })
            .addCase(googleLogin.fulfilled, (state, action) => {
                state.user = action.payload.user;
                state.token = action.payload.token;
                state.isAuthenticated = true;
            })
            .addCase(googleLogin.rejected, (state, action) => {
                state.error = typeof action.payload === "string" ? action.payload : "Google Login failed";
            })
            // Login user
            .addCase(loginUser.pending, state => {
                state.error = null;
            })
            .addCase(getUserGeneralSettingAll.fulfilled, (state, action: any) => {
                state.allgeneralSetting = action.payload;
            })
            .addCase(deleteUserGeneralSetting.fulfilled, (state, action: any) => {
                state.allgeneralSetting = state.allgeneralSetting.filter(item => item.id !== action.payload.id);
            })
            .addCase(getUserGeneralSetting.fulfilled, (state, action: any) => {
                state.generalSetting = action.payload;
            })
            .addCase(createGeneralClientSetting.fulfilled, (state, action: any) => {
                // Append new availabilities to the existing state
                state.allgeneralSetting = [...state.allgeneralSetting, action.payload];
                // Also update generalSetting if it's the only one or the first
                if (!state.generalSetting || state.allgeneralSetting.length === 1) {
                    state.generalSetting = action.payload;
                }
            })
            .addCase(UpdateClientGeneralSettings.fulfilled, (state, action: any) => {
                // Find and update the specific availability
                const updated = action.payload;
                const index = state.allgeneralSetting.findIndex(item => item.id === updated.id);
                if (index !== -1) {
                    state.allgeneralSetting[index] = updated;
                }
                // Also update generalSetting if it matches
                if (state.generalSetting && state.generalSetting.id === updated.id) {
                    state.generalSetting = updated;
                }
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                const active_user_id = parseInt(action.payload?.user?.status?.id);
                if (active_user_id == 2) {
                    state.user = null;
                    state.token = null;
                    state.isAuthenticated = false;
                } else {
                    state.user = action.payload.user;
                    state.token = action.payload.token;
                    state.isAuthenticated = true;
                }
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.error = typeof action.payload === "string" ? action.payload : "Login failed";
            })
            .addCase(UpdateUserPassword.fulfilled, (state, action) => {
                const updatedUser = action.payload;
                state.user = updatedUser;
                localStorage.setItem("user", JSON.stringify(updatedUser));
            })
            .addCase(UpdateUserProfile.fulfilled, (state, action) => {
                const updatedUser = action.payload;

                // Only update current user state if the updated user is the current user
                // This prevents admin from updating their own state when editing other users
                if (state.user && state.user.id === updatedUser.id) {
                    // Merge the updated fields with existing user data
                    state.user = { ...state.user, ...updatedUser };
                    localStorage.setItem("user", JSON.stringify(state.user));
                }

                // Always update viewedUser if it exists and matches the updated user
                if (state.viewedUser && state.viewedUser.id === updatedUser.id) {
                    state.viewedUser = { ...state.viewedUser, ...updatedUser };
                }
            })
            .addCase(sendPhoneVerificationCode.fulfilled, (state, action) => {
                // Phone verification code sent successfully
                // No state changes needed here
            })
            .addCase(verifyPhoneOTP.fulfilled, (state, action) => {
                // Update user phone verification status
                if (state.user) {
                    state.user.isPhoneVerified = true;
                }
            })
            .addCase(getAllLanguages.fulfilled, (state, action: any) => {
                // Handle both paginated and non-paginated responses
                console.log(action,"action.payload");
                if (action.payload?.data && Array.isArray(action.payload.data)) {
                    state.languages = action.payload.data;
                } else {
                    // Handle non-paginated response (backward compatibility)
                    state.languages = action.payload || [];
                }
            })
            .addCase(getAllCurrencies.fulfilled, (state, action: any) => {
                const shouldAppend = action.meta.arg.append || false;
                let newCurrencies: any[] = [];
                
                // Handle both paginated and non-paginated responses
                if (action.payload?.data && Array.isArray(action.payload.data)) {
                    newCurrencies = action.payload.data.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                        initial: item.initial,
                        exchangeRate: item.exchangeRate, // ✅ Include exchange rate
                        symbol: item.symbol, // ✅ Include currency symbol
                        code: item.initial // ✅ Add code for easier access
                    }));
                } else {
                    // Handle non-paginated response (backward compatibility)
                    const currenciesData = action.payload || [];
                    newCurrencies = currenciesData.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                        initial: item.initial,
                        exchangeRate: item.exchangeRate, // ✅ Include exchange rate
                        symbol: item.symbol, // ✅ Include currency symbol
                        code: item.initial // ✅ Add code for easier access
                    }));
                }
                
                if (shouldAppend) {
                    // Append mode: Add new currencies that don't already exist
                    const existingValues = new Set(state.currencies.map((c: any) => c.value));
                    const uniqueNewCurrencies = newCurrencies.filter((c: any) => !existingValues.has(c.value));
                    state.currencies = [...state.currencies, ...uniqueNewCurrencies];
                } else {
                    // Replace mode: Replace entire array
                    state.currencies = newCurrencies;
                }
            })
            .addCase(getAllCurrencies.rejected, (state, action: any) => {
                state.error = action.payload || "Failed to fetch currencies.";
            })
            .addCase(getCurrencyById.fulfilled, (state, action: any) => {
                // Append single currency to the list if not already present
                if (action.payload) {
                    const currencyData: any = {
                        value: action.payload.id,
                        label: action.payload.name,
                        initial: action.payload.initial,
                        exchangeRate: action.payload.exchangeRate, // ✅ Include exchange rate
                        symbol: action.payload.symbol, // ✅ Include currency symbol
                        code: action.payload.initial // ✅ Add code for easier access
                    };
                    
                    // Check if currency already exists
                    const exists = state.currencies.some((c: any) => c.value === currencyData.value);
                    
                    if (!exists) {
                        state.currencies = [...state.currencies, currencyData] as any;
                    }
                }
            })
            .addCase(getCurrencyById.rejected, (state, action: any) => {
                state.error = action.payload || "Failed to fetch currency.";
            })
            .addCase(detectUserLocation.fulfilled, (state, action: any) => {
                // Store location data from backend
                state.locationData = action.payload;
            })
            .addCase(detectUserLocation.rejected, (state, action: any) => {
                // Don't update state on error - will use cached or default
            })
            .addCase(getAllTimezones.fulfilled, (state, action: any) => {
                // Handle both paginated and non-paginated responses
                if (action.payload?.data && Array.isArray(action.payload.data)) {
                    state.timezones = action.payload.data.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                    }));
                } else {
                    // Handle non-paginated response (backward compatibility)
                    const timezonesData = action.payload || [];
                    state.timezones = timezonesData.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                    }));
                }
            })
            .addCase(uploadUserImage.fulfilled, (state, action) => {
                if (state.user) {
                    state.user.photo = action.payload.file; // ✅ update only photo
                    localStorage.setItem("user", JSON.stringify(state.user));
                }
            })
            // Get User Tickets

            .addCase(getUserTickets.fulfilled, (state, action) => {
                state.userTickets = action.payload;
            })
            // Get All Tickets
            .addCase(getAllTickets.fulfilled, (state, action) => {
                state.allTickets = action.payload;
            })

            // Create Ticket

            .addCase(createUserTickets.fulfilled, (state, action) => {
                state.userTickets = [action.payload, ...state.userTickets];
                state.allTickets.data = [action.payload, ...state.allTickets.data];
                state.allTickets.totalCount += 1;
            })

            // Update Ticket

            .addCase(updateUserTickets.fulfilled, (state, action) => {
                // Update in userTickets
                state.userTickets = state.userTickets.map(ticket =>
                    ticket.id === action.payload.id ? action.payload : ticket
                );
                // Update in allTickets
                state.allTickets.data = state.allTickets.data.map(ticket =>
                    ticket.id === action.payload.id ? action.payload : ticket
                );
            })
            .addCase(createChat.fulfilled, (state, action) => {
                state.chats = action.payload;
            })
            .addCase(getUserChats.fulfilled, (state, action) => {
                state.chats = action.payload;
            })
            .addCase(UpdateUserChats.fulfilled, (state, action) => {
                state.chats = action.payload;
            })
            // .addCase(deleteUserChat.fulfilled, (state, action) => {
            //     state.chats = state.chats.filter(chat => chat.id !== action.payload.id);
            //     // Also remove conversations associated with this chat
            //     state.conversations = state.conversations.filter(conv => conv.chatId.id !== action.payload.id);
            // })

            // Conversations
            .addCase(createConversation.fulfilled, (state, action) => {
                // Ensure conversations is always an array
                const currentConversations = Array.isArray(state.conversations) ? state.conversations : [];
                state.conversations = [action.payload, ...currentConversations];
                // Update last message in the corresponding chat
                // state.chats = state.chats.map(chat => {
                //     if (chat.id === action.payload.chatId.id) {
                //         return {
                //             ...chat,
                //             lastMessage: action.payload.message,
                //             lastMessageAt: action.payload.createdAt,
                //         };
                //     }
                //     return chat;
                // });
            })
            .addCase(getUserConversations.fulfilled, (state, action) => {
                // Ensure we always set conversations to an array
                state.conversations = Array.isArray(action.payload) ? action.payload : (action.payload?.data || []);
            })
            .addCase(getUserConversations.rejected, (state) => {
                // Ensure conversations remains an array even on error
                if (!Array.isArray(state.conversations)) {
                    state.conversations = [];
                }
            })
            .addCase(UpdateUserConversation.fulfilled, (state, action) => {
                // Ensure conversations is always an array before mapping
                const currentConversations = Array.isArray(state.conversations) ? state.conversations : [];
                state.conversations = currentConversations.map(conv =>
                    conv.id === action.payload.id ? action.payload : conv
                );
            })
            .addCase(deleteUserConversation.fulfilled, (state, action) => {
                // Ensure conversations is always an array before filtering
                const currentConversations = Array.isArray(state.conversations) ? state.conversations : [];
                state.conversations = currentConversations.filter(conv => conv.id !== action.payload.id);
            })
            // Delete Ticket

            .addCase(deleteUserTicket.fulfilled, (state, action) => {
                // Remove from userTickets
                state.userTickets = state.userTickets.filter(ticket => ticket.id !== action.payload);
                // Remove from allTickets
                state.allTickets.data = state.allTickets.data.filter(ticket => ticket.id !== action.payload);
                state.allTickets.totalCount -= 1;
            })
            // Get User By ID
            .addCase(getUserById.pending, (state) => {
                state.error = null;
            })
            .addCase(getUserById.fulfilled, (state, action) => {
                const fetchedUser = action.payload as any;
                state.viewedUser = fetchedUser;

                if (state.user && fetchedUser && state.user.id === fetchedUser.id) {
                    state.user = { ...state.user, ...fetchedUser };
                    try {
                        localStorage.setItem("user", JSON.stringify(state.user));
                    } catch (storageError) {
                        console.error("Failed to persist updated user to localStorage:", storageError);
                    }
                }
            })
            .addCase(getUserById.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            // Assign Professional WhatsApp
            .addCase(assignProfessionalWhatsApp.pending, (state) => {
                state.error = null;
            })
            .addCase(assignProfessionalWhatsApp.fulfilled, (state, action) => {
                // WhatsApp message sent successfully
                // No state changes needed here
            })
            .addCase(assignProfessionalWhatsApp.rejected, (state, action) => {
                state.error = action.payload as string;
            });
    },
});

export const { rehydrateUser, LogoutManually, EmptyConversations } = authSlice.actions;
export default authSlice.reducer;
